import { useState, useEffect } from 'react';
import { usePWA } from '../context/PWAContext';

const SplashScreen = ({ onComplete }) => {
  const { isNativeMode } = usePWA();
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    // Only show splash screen in native/PWA mode
    if (!isNativeMode) {
      setIsVisible(false);
      if (onComplete) onComplete();
      return;
    }

    // Animate out after 1.5 seconds
    const animateTimer = setTimeout(() => {
      setIsAnimatingOut(true);
    }, 1500);

    // Remove splash screen after animation
    const removeTimer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) onComplete();
    }, 2000);

    return () => {
      clearTimeout(animateTimer);
      clearTimeout(removeTimer);
    };
  }, [isNativeMode, onComplete]);

  if (!isVisible) return null;

  return (
    <div 
      className={`
        fixed inset-0 z-[99999] 
        flex flex-col items-center justify-center
        bg-zinc-950
        transition-all duration-500 ease-out
        ${isAnimatingOut ? 'opacity-0 scale-110' : 'opacity-100 scale-100'}
      `}
      data-testid="splash-screen"
    >
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      {/* Logo */}
      <div className={`
        relative z-10 flex flex-col items-center gap-6
        transition-all duration-700 ease-out
        ${isAnimatingOut ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'}
      `}>
        {/* Icon Container */}
        <div className="relative">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur-xl opacity-50 scale-110 animate-pulse" />
          
          {/* Main icon */}
          <div className="relative w-24 h-24 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl flex items-center justify-center shadow-2xl">
            {/* DamyxIA logo - stylized "D" */}
            <svg 
              viewBox="0 0 100 100" 
              className="w-14 h-14 text-white"
              fill="currentColor"
            >
              <path d="M25 15h30c16.569 0 30 13.431 30 30v10c0 16.569-13.431 30-30 30H25V15zm15 15v45h15c8.284 0 15-6.716 15-15V45c0-8.284-6.716-15-15-15H40z"/>
            </svg>
          </div>
        </div>

        {/* Text */}
        <div className="text-center">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            DamyxIA
          </h1>
          <p className="mt-2 text-sm text-zinc-500">
            Tu asistente de IA
          </p>
        </div>

        {/* Loading indicator */}
        <div className="flex gap-1.5 mt-4">
          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>

      {/* Version info */}
      <div className={`
        absolute bottom-8 text-zinc-600 text-xs
        transition-all duration-500
        ${isAnimatingOut ? 'opacity-0' : 'opacity-100'}
      `}>
        v1.0.0
      </div>
    </div>
  );
};

export default SplashScreen;
